import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Flame,
  LayoutGrid,
  List,
  Plus,
  Sliders,
  TrendingUp,
} from 'lucide-react';
import { fetchApi } from '../api';
import DealFilterBar from './deals/DealFilterBar';
import DealModal from './deals/DealModal';
import DealsBoard from './deals/DealsBoard';
import DealsTable from './deals/DealsTable';
import PipelineManagerModal from './deals/PipelineManagerModal';
import {
  DEAL_PAGE_SIZE,
  DEFAULT_DEAL_FILTERS,
  buildDealsQuery,
  hasActiveDealFilters,
  stagesOfPipeline,
  statusForStage,
  type AssigneeOption,
  type Deal,
  type DealFiltersState,
  type DealSummary,
  type Pipeline,
} from './deals/types';
import '../styles/deals.css';

interface DealsPageProps {
  setActiveView: (view: string) => void;
  onNavigateToChat?: (chatId: number) => void;
}

const EMPTY_SUMMARY: DealSummary = {
  totalCount: 0,
  openCount: 0,
  wonCount: 0,
  lostCount: 0,
  pipelineAmount: 0,
  wonAmount: 0,
  winRate: 0,
  avgCheck: 0,
};

const SEARCH_DEBOUNCE_MS = 350;

export default function DealsPage({ setActiveView, onNavigateToChat }: DealsPageProps) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [summary, setSummary] = useState<DealSummary>(EMPTY_SUMMARY);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');

  const [filters, setFilters] = useState<DealFiltersState>(DEFAULT_DEAL_FILTERS);
  const [searchInput, setSearchInput] = useState('');

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipelinesReady, setPipelinesReady] = useState(false);
  const [assignees, setAssignees] = useState<AssigneeOption[]>([]);
  const [canManagePipelines, setCanManagePipelines] = useState(false);

  const [conversations, setConversations] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);

  // Guards against an earlier, slower response overwriting a newer one when
  // filters change quickly.
  const requestIdRef = useRef(0);

  const listQuery = useMemo(
    () => buildDealsQuery(filters, { page, limit: DEAL_PAGE_SIZE, paginated: true }),
    [filters, page],
  );
  const summaryQuery = useMemo(() => buildDealsQuery(filters), [filters]);

  const activePipeline = useMemo(() => {
    if (!pipelines.length) return null;
    return pipelines.find((pipeline) => pipeline.id === filters.pipelineId) || pipelines[0];
  }, [pipelines, filters.pipelineId]);

  const activeStages = useMemo(() => stagesOfPipeline(activePipeline), [activePipeline]);
  const activeFilters = useMemo(() => hasActiveDealFilters(filters), [filters]);

  const reloadPipelines = useCallback(async () => {
    try {
      const data: Pipeline[] = await fetchApi('/pipelines');
      setPipelines(data || []);
      if (data?.length) {
        const preferred = data.find((pipeline) => pipeline.isDefault) || data[0];
        setFilters((prev) => (prev.pipelineId ? prev : { ...prev, pipelineId: preferred.id }));
      }
    } catch (error: any) {
      setErrorMessage(error?.message || 'Не удалось загрузить воронки');
    } finally {
      setPipelinesReady(true);
    }
  }, []);

  const loadDeals = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const [listResponse, summaryResponse] = await Promise.all([
        fetchApi(`/deals?${listQuery}`),
        fetchApi(`/deals/summary?${summaryQuery}`),
      ]);
      if (requestId !== requestIdRef.current) return;

      setDeals(Array.isArray(listResponse?.data) ? listResponse.data : []);
      setTotal(Number(listResponse?.total) || 0);
      setTotalPages(Number(listResponse?.totalPages) || 1);
      setSummary({ ...EMPTY_SUMMARY, ...(summaryResponse || {}) });
      setErrorMessage('');
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;
      setErrorMessage(error?.message || 'Не удалось загрузить заявки');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [listQuery, summaryQuery]);

  useEffect(() => {
    void reloadPipelines();

    fetchApi('/conversations')
      .then((data) => setConversations(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => setConversations([]));
    fetchApi('/products')
      .then((data) => setProducts(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setProducts([]));
    // Only OWNER/ADMIN may read the team, and only they may restructure pipelines,
    // so one call answers both the assignee filter and the edit affordances.
    fetchApi('/team')
      .then((data) => {
        const members = Array.isArray(data?.members) ? data.members : [];
        setAssignees(members.map((member: any) => ({
          id: member.userId || member.user?.id,
          name: member.user?.name || member.user?.email || `Участник #${member.id}`,
        })).filter((option: AssigneeOption) => Boolean(option.id)));
        setCanManagePipelines(true);
      })
      .catch(() => {
        setAssignees([]);
        setCanManagePipelines(false);
      });
  }, [reloadPipelines]);

  // The board cannot be drawn before the stages are known, so the first request
  // waits for the pipelines instead of firing twice.
  useEffect(() => {
    if (!pipelinesReady) return;
    void loadDeals();
  }, [pipelinesReady, loadDeals]);

  useEffect(() => {
    if (searchInput === filters.q) return;
    const timer = window.setTimeout(() => {
      setFilters((prev) => ({ ...prev, q: searchInput }));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput, filters.q]);

  /** Filter changes always return to the first page, in one render. */
  const updateFilters = useCallback((patch: Partial<DealFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setFilters((prev) => ({ ...DEFAULT_DEAL_FILTERS, pipelineId: prev.pipelineId }));
    setPage(1);
  }, []);

  const handleStageChange = useCallback(async (dealId: number, stageName: string) => {
    const targetStage = activeStages.find((stage) => stage.id === stageName);
    if (!targetStage) return;
    const newStatus = statusForStage(targetStage);

    const previousDeals = deals;
    setDeals((prev) => prev.map((deal) => (deal.id === dealId
      ? { ...deal, stageId: targetStage.stageId ?? deal.stageId, stage: stageName, status: newStatus }
      : deal)));
    const movedDeal = deals.find((deal) => deal.id === dealId);
    setStatusMessage(`Заявка ${movedDeal?.title || `№${dealId}`} перемещена в этап «${targetStage.label}»`);

    try {
      const updated = await fetchApi(`/deals/${dealId}`, {
        method: 'PATCH',
        body: JSON.stringify({ stageId: targetStage.stageId, stage: stageName, status: newStatus }),
      });
      setDeals((prev) => prev.map((deal) => (deal.id === dealId ? updated : deal)));
      setErrorMessage('');
      // A won/lost move changes the aggregates and may have moved stock, so the
      // cards and the catalog snapshot are refreshed rather than guessed at.
      if (newStatus !== 'open' || movedDeal?.status !== 'open') void loadDeals();
      if (newStatus === 'won') {
        fetchApi('/products')
          .then((data) => setProducts(Array.isArray(data) ? data : (data?.items || [])))
          .catch(() => undefined);
      }
    } catch (error: any) {
      // The server refuses the move when the warehouse cannot cover the deal.
      // Put the card back where it was and show why.
      setDeals(previousDeals);
      setErrorMessage(error?.message || 'Не удалось переместить заявку');
      setStatusMessage('');
    }
  }, [activeStages, deals, loadDeals]);

  const handleDeleteDeal = useCallback(async (dealId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту заявку?')) return;
    const previousDeals = deals;
    setDeals((prev) => prev.filter((deal) => deal.id !== dealId));
    try {
      await fetchApi(`/deals/${dealId}`, { method: 'DELETE' });
      void loadDeals();
    } catch (error: any) {
      setDeals(previousDeals);
      setErrorMessage(error?.message || 'Не удалось удалить заявку');
    }
  }, [deals, loadDeals]);

  const handleOpenCreateModal = useCallback(() => {
    setEditingDeal(null);
    setIsDealModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((deal: Deal) => {
    setEditingDeal(deal);
    setIsDealModalOpen(true);
  }, []);

  const handleCloseDealModal = useCallback(() => {
    setIsDealModalOpen(false);
    setEditingDeal(null);
  }, []);

  const handleDealSaved = useCallback(() => {
    setIsDealModalOpen(false);
    setEditingDeal(null);
    setErrorMessage('');
    void loadDeals();
  }, [loadDeals]);

  const handleGoToChat = useCallback((chatId?: number | null) => {
    if (!chatId) return;
    if (onNavigateToChat) onNavigateToChat(chatId);
    else setActiveView('chats');
  }, [onNavigateToChat, setActiveView]);

  const handleSelectPipeline = useCallback((pipelineId: number) => {
    updateFilters({ pipelineId, stageId: null });
  }, [updateFilters]);

  const handlePipelinesChanged = useCallback(() => {
    void reloadPipelines();
    void loadDeals();
  }, [reloadPipelines, loadDeals]);

  const handleOpenPipelineModal = useCallback(() => setIsPipelineModalOpen(true), []);
  const handleClosePipelineModal = useCallback(() => setIsPipelineModalOpen(false), []);
  const handleKanbanView = useCallback(() => setViewMode('kanban'), []);
  const handleTableView = useCallback(() => setViewMode('table'), []);
  const handleDismissError = useCallback(() => setErrorMessage(''), []);
  const handlePrevPage = useCallback(() => setPage((current) => Math.max(1, current - 1)), []);
  const handleNextPage = useCallback(() => setPage((current) => current + 1), []);

  const firstOnPage = total === 0 ? 0 : (page - 1) * DEAL_PAGE_SIZE + 1;
  const lastOnPage = Math.min(page * DEAL_PAGE_SIZE, total);

  return (
    <div className="deals-page">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{statusMessage}</div>

      <div className="deals-header">
        <div>
          <h1 className="deals-title">Заявки</h1>
          <p className="deals-subtitle">Управление воронкой продаж, откликами клиентов и этапами сделок</p>
        </div>
        <div className="deals-header-actions">
          <div className="view-mode-toggle">
            <button type="button" className={`vm-btn ${viewMode === 'kanban' ? 'active' : ''}`} onClick={handleKanbanView} title="Канбан-доска">
              <LayoutGrid size={16} />
              <span>Канбан</span>
            </button>
            <button type="button" className={`vm-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={handleTableView} title="Таблица">
              <List size={16} />
              <span>Таблица</span>
            </button>
          </div>
          <button type="button" className="btn-pipeline-settings" onClick={handleOpenPipelineModal}>
            <Sliders size={16} />
            <span>Настроить воронки</span>
          </button>
          <button type="button" className="btn-primary-deal" onClick={handleOpenCreateModal}>
            <Plus size={18} />
            <span>Создать заявку</span>
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="deals-error-banner" role="alert">
          <AlertTriangle size={16} />
          <span>{errorMessage}</span>
          <button type="button" onClick={handleDismissError} aria-label="Скрыть сообщение">×</button>
        </div>
      )}

      <div className="deals-metrics-grid">
        <div className="metric-card">
          <div className="mc-icon purple"><TrendingUp size={20} /></div>
          <div className="mc-info">
            <span className="mc-label">Общая сумма воронки</span>
            <b className="mc-val">{summary.pipelineAmount.toLocaleString('ru-RU')} ₽</b>
            <span className="mc-sub">{summary.openCount} активных сделок</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="mc-icon green"><CheckCircle2 size={20} /></div>
          <div className="mc-info">
            <span className="mc-label">Выиграно сделок</span>
            <b className="mc-val">{summary.wonAmount.toLocaleString('ru-RU')} ₽</b>
            <span className="mc-sub">{summary.wonCount} сделок успешны</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="mc-icon blue"><DollarSign size={20} /></div>
          <div className="mc-info">
            <span className="mc-label">Средний чек</span>
            <b className="mc-val">{summary.avgCheck.toLocaleString('ru-RU')} ₽</b>
            <span className="mc-sub">в успешных продажах</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="mc-icon amber"><Flame size={20} /></div>
          <div className="mc-info">
            <span className="mc-label">Конверсия воронки</span>
            <b className="mc-val">{summary.winRate}%</b>
            <span className="mc-sub">{summary.totalCount} заявок обработано</span>
          </div>
        </div>
      </div>

      <DealFilterBar
        filters={filters}
        searchInput={searchInput}
        pipelines={pipelines}
        stages={activeStages}
        assignees={assignees}
        hasActiveFilters={activeFilters}
        onSearchInput={setSearchInput}
        onChange={updateFilters}
        onReset={handleResetFilters}
      />

      {loading ? (
        <div className="deals-loading-state">
          <div className="spinner" />
          <span>Загрузка воронки заявок...</span>
        </div>
      ) : viewMode === 'kanban' ? (
        <DealsBoard
          deals={deals}
          stages={activeStages}
          onStageChange={handleStageChange}
          onEdit={handleOpenEditModal}
          onDelete={handleDeleteDeal}
          onGoToChat={handleGoToChat}
        />
      ) : (
        <DealsTable
          deals={deals}
          stages={activeStages}
          onStageChange={handleStageChange}
          onEdit={handleOpenEditModal}
          onDelete={handleDeleteDeal}
          onGoToChat={handleGoToChat}
        />
      )}

      {!loading && (
        <div className="deals-pagination">
          <span className="deals-pagination-info">
            {total === 0
              ? (activeFilters ? 'По заданным фильтрам заявок нет' : 'Заявок пока нет')
              : `Показано ${firstOnPage}–${lastOnPage} из ${total} заявок`}
          </span>
          {total > 0 && (
            <div className="deals-pagination-controls">
              <button type="button" className="btn btn-outline btn-sm" onClick={handlePrevPage} disabled={page <= 1}>
                <ChevronLeft size={14} /> Назад
              </button>
              <span className="deals-pagination-page">Страница {page} из {totalPages}</span>
              <button type="button" className="btn btn-outline btn-sm" onClick={handleNextPage} disabled={page >= totalPages}>
                Вперёд <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {isDealModalOpen && (
        <DealModal
          key={editingDeal?.id ?? 'new'}
          editingDeal={editingDeal}
          stages={activeStages}
          conversations={conversations}
          products={products}
          onClose={handleCloseDealModal}
          onSaved={handleDealSaved}
          onError={setErrorMessage}
        />
      )}

      {isPipelineModalOpen && (
        <PipelineManagerModal
          pipelines={pipelines}
          selectedPipelineId={filters.pipelineId}
          canManage={canManagePipelines}
          onSelectPipeline={handleSelectPipeline}
          onPipelinesChanged={handlePipelinesChanged}
          onClose={handleClosePipelineModal}
          onError={setErrorMessage}
        />
      )}
    </div>
  );
}
